import * as React from 'react';

import {Button} from '../../../button/button';
import {EditableTextfieldContent} from '../../../editable-textfield-content/editable-textfield-content';
import {Stack} from '../../../layout/stack/stack';
import {Popover} from '../../../popover/popover';
import {SelectPickerLabel} from '../../../select-picker-label/select-picker-label';
import {TextField} from '../../../textfield/textfield';

type Props = {
    currency: string;
    amount: number | null | undefined;
    currencyOptions: string[];
    onUpdateCurrency: (currency: string, amount?: number | null | undefined) => void;
    isEditing: boolean;
    setIsEditing: (isEditing: boolean) => void;
};

export function CustomFieldCurrency(props: Props) {
    const {onUpdateCurrency, setIsEditing} = props;

    const [isOtherSelected, setIsOtherSelected] = React.useState<boolean>(false);
    const [customCurrencyCode, setCustomCurrencyCode] = React.useState<string>('');

    const currencyCodeRef = React.useRef<HTMLDivElement | null | undefined>(null);

    const handleCurrencyUpdate = React.useCallback(
        (newCurrency: string, newAmount?: number | null) => {
            if (newCurrency === 'Other') {
                setIsOtherSelected(true);

                return;
            }
            if (newCurrency !== props.currency || newAmount !== props.amount) {
                onUpdateCurrency(newCurrency, newAmount);
            } else {
                setIsEditing(false);
            }
        },
        [onUpdateCurrency, props.currency, props.amount, setIsEditing]
    );

    const handleCustomCurrencyCodeUpdate = React.useCallback(() => {
        if (customCurrencyCode) {
            setIsOtherSelected(false);
            onUpdateCurrency(customCurrencyCode, props.amount);
        }
    }, [onUpdateCurrency, customCurrencyCode, props.amount]);

    const currencyOptions = props.currencyOptions.map((option) => ({
        label: option,
        value: option,
    }));

    currencyOptions.push({
        label: 'Other',
        value: 'Other',
    });

    return (
        <>
            <Stack isHorizontal={true} spacing={8} alignItems='center'>
                {/* @ts-expect-error */}
                <div ref={currencyCodeRef}>
                    <SelectPickerLabel
                        options={currencyOptions}
                        selectedOption={
                            props.currency
                                ? {label: props.currency, value: props.currency}
                                : undefined
                        }
                        onChange={(option) => handleCurrencyUpdate(option.value, props.amount)}
                        isDisabled={isOtherSelected || props.amount == null}
                        color='dark'
                        size='small'
                    />
                </div>
                <EditableTextfieldContent
                    isEditing={props.isEditing}
                    setIsEditing={setIsEditing}
                    value={props.amount != null ? props.amount.toFixed(2) : ''}
                    displayValue={
                        props.amount != null
                            ? props.amount.toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                              })
                            : ''
                    }
                    onUpdate={(amount) => handleCurrencyUpdate(props.currency, Number(amount))}
                    placeholder='Add amount…'
                    isMultiline={false}
                    preventHideOnBlur={true}
                />
            </Stack>
            {isOtherSelected && (
                <Popover
                    anchor={currencyCodeRef.current}
                    location='bottom'
                    onBlur={(e) => {
                        e.stopPropagation();
                        setIsOtherSelected(false);
                    }}
                >
                    <div
                        className='flex flex-dir-col gap-12 pad-12'
                        onClick={(e) => e.stopPropagation()}
                    >
                        <TextField
                            value={customCurrencyCode}
                            onChange={(value) => setCustomCurrencyCode(value)}
                            onEnter={handleCustomCurrencyCodeUpdate}
                            onEscape={() => setIsOtherSelected(false)}
                            placeholder='Enter currency code'
                        />
                        <Stack
                            isHorizontal={true}
                            justifyContent='space-between'
                            alignItems='center'
                        >
                            <Button
                                onClick={() => {
                                    setIsOtherSelected(false);
                                }}
                                variant='secondary'
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={() => {
                                    handleCustomCurrencyCodeUpdate();
                                }}
                                disabled={!customCurrencyCode}
                                variant='save'
                            >
                                Save
                            </Button>
                        </Stack>
                    </div>
                </Popover>
            )}
        </>
    );
}
